import { dropdown } from '/dropdown.js';
import { vq } from '/visits.js';
import { t, tr, tParts, onLang, errorText } from '/i18n.js';
// A floor is another server-scoped Office, not a local visibility filter. Reloading deliberately closes the old
// socket and media before connecting to the new Office; no old-floor tracks/people are carried across.
// On the page floors are boats (artin 11-02: 叫楼还是 deck 吗): floor 1 is the office raft (Deck), floors admins add
// are 「N 号船」, and the 活动舱 (games boat, src/office/floors.ts PLAY_FLOOR, kind "play") is every office's, built in.
// The ids in the URL (?floor=N) and for agents (--floor N) are the floors' as before.
/** The 活动舱's floor id (src/office/floors.ts PLAY_FLOOR): its page loads /play.tmj, not /map.tmj. */
export const PLAY_FLOOR = 20;
/** The games boat's name (one catalog key, floors.name.play; the texts that name it take it as {play}). */
export const playName = () => t('floors.name.play');
/** What a boat is called on the page (the games boat: always its catalog name, not the name the server sends). */
export function boatName(f) {
  if (f.kind === 'play') return playName();
  if (f.id === 1) return f.name === '1F' ? t('floors.name.office') : t('floors.name.officeNamed', { name: f.name });
  return f.name === `${f.id}F` ? t('floors.name.boat', { id: f.id }) : t('floors.name.boatNamed', { id: f.id, name: f.name });
}
/** The floors (id, name, room), once the list came: for naming a room ('2F', or '2F · 名字'). */
export let FLOORS = [];
/** Read the floor list again (a floor made since this page loaded: a call member is up there), at most every 30 s. */
let refreshing = null, refreshedAt = 0;
export function refreshFloors() {
  if (refreshing || Date.now() - refreshedAt < 30_000) return refreshing ?? Promise.resolve();
  refreshedAt = Date.now();
  refreshing = fetch('/api/v1/offices/main/floors?boats=1', { signal: AbortSignal.timeout(8000) }).then((r) => (r.ok ? r.json() : null))
    .then((j) => { if (Array.isArray(j?.floors)) FLOORS = j.floors; }).catch(() => {}).finally(() => { refreshing = null; });
  return refreshing;
}
export const floorName = (room) => { const f = FLOORS.find((x) => x.room === room); return f ? boatName(f) : null; };
// sail(name): the page's 换船 transition (public/voyage.js), when it is on board: it covers the page and keeps what the
// next one needs to board by itself; answers how long to wait (ms) before leaving, or 0 (on the 上船 card: just go).
export async function initFloors({ addSettingsSection, toast, canLeave, leave, sail = () => 0, beforeSwitch = () => undefined }) {
  const wanted = new URL(location.href).searchParams.get('floor') ?? '1';
  const id = /^([1-9]|1[0-9]|20)$/.test(wanted) ? Number(wanted) : 0;
  let config, current;
  try {
    const r = await fetch(vq('/api/v1/offices/main/floors?boats=1'), {signal:AbortSignal.timeout(8000)});
    if (!r.ok) throw Object.assign(Error(t('floors.load.failed')), { key: 'floors.load.failed' });
    config = await r.json(); FLOORS = config.floors; current = config.floors.find(f=>f.id===id);
    if (!current || !/^[a-z0-9-]{1,40}$/.test(current.room)) throw Object.assign(Error(t('floors.load.missing')), { key: 'floors.load.missing' });
  } catch(e) {
    if(id===1){toast(t('floors.load.fallback'));return {id:1,name:'1F',room:'main',board:()=>false};}
    // (e.key: one of ours, drawn again in a new language; else the browser's own message, e.g. a timeout)
    const box=document.createElement('p'),back=tr(document.createElement('a'),'floors.load.back');back.href='/';
    const draw=()=>box.replaceChildren(...tParts('floors.load.box',{back},{reason:e.key?t(e.key):e.message}));
    draw();onLang(draw);document.getElementById('join')?.append(box);
    return {room:null,error:e.message,board:()=>false};
  }
  // arrive: the 码头 / 跳板 (a portal id on that boat's map) to land by (public/boats.js; the office reads ?arrive= on
  // the socket). confirmed: already asked (a new boat just made).
  const switchTo = (floor, confirmed=false, arrive=null) => {
    if (floor.id===current.id) return false;
    if (!confirmed&&!canLeave()) return false;
    const url=new URL(location.href);url.searchParams.set('floor',String(floor.id));
    if(arrive)url.searchParams.set('arrive',arrive);else url.searchParams.delete('arrive');
    const go=()=>{const next=()=>{leave();location.assign(url);};try{const pending=beforeSwitch(floor.id);if(pending?.then)pending.then(next,next);else next();}catch{next();}};
    const wait=sail(boatName(floor),!!arrive);if(wait>0)setTimeout(go,wait);else go(); // (arrive: walked across, a short cover)
    return true;
  };
  /** Walk across to the other boat: to "play" (the 活动舱) or "main" (the office raft). False if it can't be done. */
  current.board = (to, arrive) => {
    const f = to === 'play' ? config.floors.find((x) => x.kind === 'play') : config.floors.find((x) => x.id === 1);
    if (!f) { toast(t('floors.noPlay', { play: playName() })); return false; }
    return switchTo(f, false, arrive);
  };
  // the floor switcher: the shared dropdown (public/dropdown.js), a pill like its neighbours in the top bar
  function picker(className) {
    const dd=dropdown({label:t('floors.picker.label'),title:t('floors.picker.title'),className,value:current.id,
      options:config.floors.map(f=>({value:f.id,text:boatName(f)})),
      onChange:(v)=>{const f=config.floors.find(f=>f.id===v);dd.set(current.id);if(f)switchTo(f);}});
    return dd.el;
  }
  let top=picker('dd-pill');top.id='floor-picker';document.getElementById('topleft')?.append(top);
  // (the dropdown takes its words once: a new one in the new language)
  onLang(()=>{const n=picker('dd-pill');n.id='floor-picker';top.replaceWith(n);top=n;});
  const style=document.createElement('style');style.textContent='#floor-picker{max-width:200px}#settings .dd-floor{margin:2px 0 6px}';document.head.append(style);
  addSettingsSection({id:'floors',title:()=>t('floors.title'),order:15,render(box){
    const p=document.createElement('p');p.textContent=t('floors.about', { play: playName() });const pick=picker('dd-pill dd-floor');box.append(p,pick);
    if(!config.canEdit)return;
    const create=document.createElement('button'),status=document.createElement('p');create.textContent=t('floors.create');
    let pendingCreation=null;
    create.onclick=async()=>{
      const name=prompt(t('floors.createName'));if(name===null||!canLeave())return;create.disabled=true;status.textContent=t('floors.creating');
      if(!pendingCreation||pendingCreation.name!==name)pendingCreation={name,requestId:crypto.randomUUID()};
      try{const r=await fetch('/api/v1/offices/main/floors',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(pendingCreation)});const b=await r.json();if(!r.ok)throw Error(errorText('floors.err',b.error,{play:playName()}));config.floors=b.floors;status.textContent=t('floors.created');pendingCreation=null;switchTo(b.floor,true);}
      catch(e){status.textContent=e.message;toast(e.message);}finally{create.disabled=false;}
    };box.append(create,status);
  }});
  // (日历's 去会议室 on another floor: the same leave as the picker, public/calendar.js)
  return {...current, go:(id)=>{const f=config.floors.find(f=>f.id===id);if(f)switchTo(f);return !!f;}};
}
